import React, { useState, useEffect, useRef } from "react";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  BsLock,
  BsUnlock,
  BsMegaphone,
  BsLink45Deg,
  BsChatDots,
  BsFileText,
  BsCalendarEvent,
  BsBarChart,
  BsHeart,
  BsHeartFill,
  BsPlus,
  BsPin,
  BsThreeDotsVertical,
  BsTrash,
  BsPencil,
  BsCheckCircleFill,
  BsGlobe,
} from "react-icons/bs";
import { HiOutlineSparkles } from "react-icons/hi";
import { Oval } from "react-loader-spinner";
import { formatDistanceToNow } from "date-fns";

import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import ContentEditor from "./ContentEditor";
import {
  useGetCommunityContentQuery,
  useLikeCommunityContentMutation,
  useDeleteCommunityContentMutation,
  useVotePollContentMutation,
} from "../../redux/slices/api/communityApiSlice";
import notify from "../common/notify";
import DiscussionThread from "./DiscussionThread";

// Content type icons and labels
const contentTypeConfig = {
  announcement: { icon: BsMegaphone, label: "Announcement", color: "text-purple-500 bg-purple-50" },
  link: { icon: BsLink45Deg, label: "Links", color: "text-blue-500 bg-blue-50" },
  discussion: { icon: BsChatDots, label: "Discussion", color: "text-green-500 bg-green-50" },
  resource: { icon: BsFileText, label: "Resource", color: "text-orange-500 bg-orange-50" },
  event: { icon: BsCalendarEvent, label: "Event", color: "text-red-500 bg-red-50" },
  poll: { icon: BsBarChart, label: "Poll", color: "text-amber-500 bg-amber-50" },
};

// Locked view - shown to non-members
const LockedSection = ({ contentCount, onJoinClick, community }) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl border-2 border-dashed border-gray-300 p-8 text-center">
    <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg">
      <BsLock className="text-4xl text-gray-400" />
    </div>
    <h3 className="text-xl font-bold text-gray-800 mb-2">Members Only Content</h3>
    <p className="text-gray-600 mb-4 max-w-md mx-auto">
      This community has <span className="font-semibold text-primary">{contentCount || 0}</span> exclusive posts, 
      announcements, and resources. Join to unlock access!
    </p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button
        onClick={onJoinClick}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
      >
        <HiOutlineSparkles />
        Get Membership NFT
      </button>
    </div>
    <p className="text-xs text-gray-400 mt-4">
      {community?.saleSettings?.price || 0} {community?.saleSettings?.currency || "ETH"} for lifetime access
    </p>
  </div>
);

// Single content item
const ContentItem = ({ content, isCreator, onLike, onEdit, onDelete, communityId, walletAddress, currentUserId, readOnly = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [votingOption, setVotingOption] = useState(null);
  const [likeContent] = useLikeCommunityContentMutation();
  const [deleteContent, { isLoading: isDeleting }] = useDeleteCommunityContentMutation();
  const [votePoll] = useVotePollContentMutation();

  const config = contentTypeConfig[content.type] || contentTypeConfig.announcement;
  const Icon = config.icon;

  const handleLike = async () => {
    try {
      await likeContent({ id: communityId, contentId: content._id }).unwrap();
    } catch (err) {
      notify("error", "Failed to like content");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;
    try {
      await deleteContent({ id: communityId, contentId: content._id }).unwrap();
      notify("success", "Content deleted");
    } catch (err) {
      notify("error", "Failed to delete content");
    }
    setShowMenu(false);
  };

  const handleVote = async (optionIndex) => {
    if (!walletAddress) {
      notify("error", "Please connect your wallet to vote");
      return;
    }
    setVotingOption(optionIndex);
    try {
      await votePoll({ id: communityId, contentId: content._id, optionIndex }).unwrap();
    } catch (err) {
      notify("error", err?.data?.message || "Failed to vote");
    }
    setVotingOption(null);
  };

  // Poll helpers
  const pollOptions = content.pollOptions || [];
  const totalVotes = pollOptions.reduce((sum, opt) => sum + (opt.votes || 0), 0);
  const userVote = content.pollVoters?.find(
    (v) => v.wallet?.toLowerCase() === walletAddress?.toLowerCase()
  );
  const pollEnded = content.pollSettings?.endsAt && new Date(content.pollSettings.endsAt) < new Date();

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow ${content.pinned ? "ring-2 ring-purple-200" : ""}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <img
          src={content.author?.images?.profile || UserPlaceHolderImg}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800 truncate">
              {content.author?.fundername || content.author?.firstname || "Anonymous"}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${config.color}`}>
              <Icon className="inline mr-1" />
              {config.label}
            </span>
            {content.pinned && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
                <BsPin className="inline mr-1" />
                Pinned
              </span>
            )}
            {content.isPublic && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 flex items-center gap-1">
                <BsGlobe className="inline" />
                Public
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(content.createdAt), { addSuffix: true })}
          </p>
        </div>

        {/* Creator actions */}
        {isCreator && !readOnly && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <BsThreeDotsVertical className="text-gray-400" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20 min-w-[140px]">
                  <button
                    onClick={() => {
                      onEdit(content);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <BsPencil />
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <BsTrash />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Title & Content */}
      <h4 className="font-semibold text-gray-800 mb-2">{content.title}</h4>
      {content.content && (
        <p className="text-gray-600 text-sm whitespace-pre-wrap mb-3">{content.content}</p>
      )}

      {/* Links */}
      {content.links?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {content.links.map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors"
            >
              <BsLink45Deg />
              {link.title || "Link"}
            </a>
          ))}
        </div>
      )}

      {/* Event Details */}
      {content.type === "event" && content.eventDetails?.date && (
        <div className="bg-red-50 rounded-xl p-3 mb-3">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <BsCalendarEvent />
            <span>
              {new Date(content.eventDetails.date).toLocaleDateString(undefined, {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          {content.eventDetails.location && (
            <p className="text-sm text-red-600 mt-1 ml-6">{content.eventDetails.location}</p>
          )}
        </div>
      )}

      {/* Poll Options */}
      {content.type === "poll" && pollOptions.length > 0 && (
        <div className="bg-amber-50 rounded-xl p-4 mb-3">
          <div className="space-y-2">
            {pollOptions.map((option, idx) => {
              const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
              const isVoted = userVote?.optionIndex === idx;
              const canVote = !readOnly && !pollEnded && !userVote;

              return (
                <button
                  key={idx}
                  onClick={() => canVote && handleVote(idx)}
                  disabled={!canVote || votingOption !== null}
                  className={`w-full relative overflow-hidden rounded-lg border transition-all ${
                    isVoted
                      ? "border-amber-500 bg-amber-100"
                      : canVote
                      ? "border-gray-200 bg-white hover:border-amber-300 cursor-pointer"
                      : "border-gray-200 bg-white cursor-default"
                  }`}
                >
                  {/* Progress bar background */}
                  {(userVote || pollEnded) && (
                    <div
                      className="absolute inset-y-0 left-0 bg-amber-200/50 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  )}
                  <div className="relative px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isVoted && <BsCheckCircleFill className="text-amber-600" />}
                      {votingOption === idx && (
                        <Oval width={16} height={16} strokeWidth={4} color="#f59e0b" />
                      )}
                      <span className={`text-sm ${isVoted ? "font-medium text-amber-800" : "text-gray-700"}`}>
                        {option.text}
                      </span>
                    </div>
                    {(userVote || pollEnded) && (
                      <span className="text-sm font-medium text-amber-700">
                        {percentage}%
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-amber-700">
            <span>{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</span>
            {content.pollSettings?.endsAt && (
              <span>
                {pollEnded
                  ? "Poll ended"
                  : `Ends ${formatDistanceToNow(new Date(content.pollSettings.endsAt), { addSuffix: true })}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
        {readOnly ? (
          <span className="flex items-center gap-1.5 text-sm text-gray-400">
            <BsHeart />
            <span>{content.likes || 0}</span>
          </span>
        ) : (
          <button
            onClick={handleLike}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors"
          >
            {content.likedBy?.includes(content.currentUserId) ? (
              <BsHeartFill className="text-red-500" />
            ) : (
              <BsHeart />
            )}
            <span>{content.likes || 0}</span>
          </button>
        )}
      </div>

      {/* Discussion thread — only for discussion-type posts and members */}
      {content.type === "discussion" && !readOnly && (
        <DiscussionThread
          communityId={communityId}
          contentId={content._id}
          isCreator={isCreator}
          walletAddress={walletAddress}
          commentCount={content.commentCount || 0}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};

// Main component
const PremiumContentSection = ({ community, isMember, isCreator, isAdmin, onJoinClick, currentUserId }) => {
  const { address } = useAccount();
  const [showEditor, setShowEditor] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const {
    data: contentData,
    isLoading,
    refetch,
  } = useGetCommunityContentQuery(
    {
      id: community?._id,
      walletAddress: address,
      type: activeFilter === "all" ? undefined : activeFilter,
    },
    { skip: !community?._id }
  );

  const handleEditContent = (content) => {
    setEditingContent(content);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingContent(null);
    refetch();
  };

  // When the creator gets a cached locked response (stale from an anonymous
  // visit with the same query args), force a fresh fetch so they see their
  // own content.
  const refetchedForCreator = useRef(false);
  useEffect(() => {
    if ((isCreator || isAdmin) && contentData?.locked && !refetchedForCreator.current) {
      refetchedForCreator.current = true;
      refetch();
    }
  }, [isCreator, isAdmin, contentData?.locked, refetch]);

  // Show locked state if not a member (creators and admins always bypass this)
  if (contentData?.locked && !isCreator && !isAdmin) {
    const publicPosts = contentData.publicContent || [];
    const privateCount = contentData.privateCount ?? (contentData.contentCount || 0);
    return (
      <div className="mt-8">
        {/* Public posts — visible to everyone */}
        {publicPosts.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BsGlobe className="text-green-500" />
              Public Posts
            </h3>
            <div className="space-y-4">
              {publicPosts.map((item) => (
                <ContentItem
                  key={item._id}
                  content={item}
                  isCreator={false}
                  communityId={community._id}
                  walletAddress={null}
                  currentUserId={null}
                  readOnly
                />
              ))}
            </div>
          </div>
        )}

        {/* Private content teaser */}
        {privateCount > 0 && (
          <>
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BsLock className="text-gray-400" />
              Exclusive Content
            </h3>
            <LockedSection
              contentCount={privateCount}
              onJoinClick={onJoinClick}
              community={community}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <BsUnlock className="text-green-500" />
          Member Content
        </h3>
        {(isCreator || isAdmin) && (
          <button
            onClick={() => setShowEditor(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <BsPlus className="text-lg" />
            Add Content
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveFilter("all")}
          className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
            activeFilter === "all"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All
        </button>
        {Object.entries(contentTypeConfig).map(([type, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={type}
              onClick={() => setActiveFilter(type)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                activeFilter === type
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Icon />
              {config.label}
            </button>
          );
        })}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Oval height={40} width={40} color="#8538ff" secondaryColor="#8538ff33" />
        </div>
      )}

      {/* Content list */}
      {!isLoading && contentData?.content?.length > 0 && (
        <div className="space-y-4">
          {contentData.content.map((item) => (
            <ContentItem
              key={item._id}
              content={item}
              isCreator={isCreator || isAdmin}
              communityId={community._id}
              walletAddress={address}
              currentUserId={currentUserId}
              onEdit={handleEditContent}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!contentData?.content || contentData.content.length === 0) && (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <BsMegaphone className="text-4xl text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No content yet</p>
          {(isCreator || isAdmin) && (
            <button
              onClick={() => setShowEditor(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:opacity-90"
            >
              <BsPlus />
              Create First Post
            </button>
          )}
        </div>
      )}

      {/* Content Editor Modal */}
      {showEditor && (
        <ContentEditor
          communityId={community._id}
          editingContent={editingContent}
          onClose={handleEditorClose}
        />
      )}
    </div>
  );
};

export default PremiumContentSection;
